import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import geoService from '../../services/geo.service';
import villageService from '../../services/Villages.service';
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';

const AddVillage = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [form, setForm] = useState({
    state: '', district: '', taluka: '', villageId: '',
  });
  const [states,    setStates]    = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas,   setTalukas]   = useState([]);
  const [villages,  setVillages]  = useState([]);
  const [pickedVillage, setPickedVillage] = useState(null);

  const [loading, setLoading] = useState({ states: false, dist: false, tal: false, vill: false });
  const [errors,  setErrors]  = useState({});
  const [toast,   setToast]   = useState(null);

  // "Add new village" mini-form
  const [showAddVillage, setShowAddVillage] = useState(false);
  const [newVillageName, setNewVillageName] = useState('');
  const [newVillageCode, setNewVillageCode] = useState('');
  const [isAddingVillage, setIsAddingVillage] = useState(false);

  // ── Load states once ─────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(l => ({ ...l, states: true }));
    geoService.getStates()
      .then(r => { if (r.success) setStates(r.data || []); })
      .catch(() => setToast({ message: 'Failed to load states', type: 'error' }))
      .finally(() => setLoading(l => ({ ...l, states: false })));
  }, []);

  // ── State → districts ────────────────────────────────────────────────────
  useEffect(() => {
    setDistricts([]); setTalukas([]); setVillages([]); setPickedVillage(null);
    setForm(f => ({ ...f, district: '', taluka: '', villageId: '' }));
    setShowAddVillage(false);
    if (!form.state) return;

    setLoading(l => ({ ...l, dist: true }));
    geoService.getDistricts(form.state)
      .then(r => { if (r.success) setDistricts(r.data || []); })
      .finally(() => setLoading(l => ({ ...l, dist: false })));
  }, [form.state]);

  // ── District → talukas ───────────────────────────────────────────────────
  useEffect(() => {
    setTalukas([]); setVillages([]); setPickedVillage(null);
    setForm(f => ({ ...f, taluka: '', villageId: '' }));
    setShowAddVillage(false);
    if (!form.state || !form.district) return;

    setLoading(l => ({ ...l, tal: true }));
    geoService.getTalukas(form.state, form.district)
      .then(r => { if (r.success) setTalukas(r.data || []); })
      .finally(() => setLoading(l => ({ ...l, tal: false })));
  }, [form.district]);

  // ── Taluka → villages ────────────────────────────────────────────────────
  useEffect(() => {
    setVillages([]); setPickedVillage(null);
    setForm(f => ({ ...f, villageId: '' }));
    setShowAddVillage(false);
    if (!form.state || !form.district || !form.taluka) return;

    setLoading(l => ({ ...l, vill: true }));
    geoService.getVillages(form.state, form.district, form.taluka)
      .then(r => { if (r.success) setVillages(r.data || []); })
      .finally(() => setLoading(l => ({ ...l, vill: false })));
  }, [form.taluka]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleVillageSelect = (e) => {
    const villageId = e.target.value;
    setForm(f => ({ ...f, villageId }));
    const v = villages.find(v => String(v.id) === String(villageId));
    setPickedVillage(v || null);
    if (errors.villageId) setErrors(prev => ({ ...prev, villageId: '' }));
  };

  const handleAddNewVillage = async () => {
    if (!newVillageName.trim()) {
      setErrors(prev => ({ ...prev, newVillageName: 'Enter a village name' }));
      return;
    }
    setIsAddingVillage(true);
    try {
      const res = await geoService.addManualVillage({
        name: newVillageName.trim(),
        stateName: form.state,
        districtName: form.district,
        talukaName: form.taluka,
        villageCode: newVillageCode.trim() || undefined,
      });
      if (res.success) {
        const created = res.data;
        setVillages(prev => [...prev, { id: created.id, name: created.name, code: created.villageCode }]);
        setForm(f => ({ ...f, villageId: created.id }));
        setPickedVillage(created);
        setShowAddVillage(false);
        setNewVillageName('');
        setNewVillageCode('');
        setErrors(prev => ({ ...prev, villageId: '', newVillageName: '' }));
        setToast({ message: `"${created.name}" added`, type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to add village', type: 'error' });
    } finally {
      setIsAddingVillage(false);
    }
  };

  const handleDone = () => {
    if (!pickedVillage) {
      setErrors(prev => ({ ...prev, villageId: 'Pick or add a village first' }));
      return;
    }
    navigate('/villages/list');
  };

  return (
    <div className="add-village-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/villages/list">Villages</a></li>
          <li className="breadcrumb-item active">Add Village</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Add New Village</h4>
        <p style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>
          Pick State → District → Tehsil. If your village isn't listed, add it right here.
        </p>
      </div>

      <div className="form-card">
        <div className="form-section">
          <h3 className="section-title">📍 Government Records</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">State <span className="required">*</span></label>
              {loading.states ? (
                <div className="form-input" style={{ color: '#adb5bd' }}>Loading states...</div>
              ) : (
                <select className="form-input"
                  value={form.state}
                  onChange={(e) => setForm(f => ({ ...f, state: e.target.value }))}>
                  <option value="">Select State</option>
                  {states.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                </select>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">District <span className="required">*</span></label>
              {loading.dist ? (
                <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
              ) : (
                <select className="form-input"
                  value={form.district}
                  onChange={(e) => setForm(f => ({ ...f, district: e.target.value }))}
                  disabled={!form.state}>
                  <option value="">{!form.state ? 'Select State first' : 'Select District'}</option>
                  {districts.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
                </select>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Tehsil/Taluka <span className="required">*</span></label>
              {loading.tal ? (
                <div className="form-input" style={{ color: '#adb5bd' }}>Loading...</div>
              ) : (
                <select className="form-input"
                  value={form.taluka}
                  onChange={(e) => setForm(f => ({ ...f, taluka: e.target.value }))}
                  disabled={!form.district}>
                  <option value="">{!form.district ? 'Select District first' : 'Select Tehsil'}</option>
                  {talukas.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-full">
              <label className="form-label">Village</label>
              {loading.vill ? (
                <div className="form-input" style={{ color: '#adb5bd' }}>Loading villages...</div>
              ) : (
                <select
                  className={`form-input ${errors.villageId ? 'error' : ''}`}
                  value={form.villageId} onChange={handleVillageSelect}
                  disabled={!form.taluka}>
                  <option value="">
                    {!form.taluka
                      ? 'Select Tehsil first'
                      : villages.length === 0
                        ? 'No villages found'
                        : `${villages.length} villages — pick one`}
                  </option>
                  {villages.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.name}{v.code ? ` (Code: ${v.code})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {errors.villageId && <span className="error-message">{errors.villageId}</span>}
            </div>
          </div>

          {pickedVillage && (
            <div style={{
              padding: '12px 16px', borderRadius: 8, marginTop: 12,
              background: '#e8f5e9', border: '1px solid #a5d6a7',
              fontSize: 13, color: '#1b5e20',
            }}>
              ✅ <strong>{pickedVillage.name}</strong>
              {pickedVillage.code && <> — Code: <code>{pickedVillage.code}</code></>}
              <br/>{form.taluka}, {form.district}, {form.state}
            </div>
          )}

          {/* Can't find your village? */}
          {form.taluka && !loading.vill && (
            <div style={{ marginTop: 16 }}>
              {!showAddVillage ? (
                <button type="button" className="btn btn-link" style={{ padding: 0, fontSize: 13 }}
                  onClick={() => setShowAddVillage(true)}>
                  ➕ Can't find your village? Add it under {form.taluka}
                </button>
              ) : (
                <div style={{
                  padding: 16, borderRadius: 8, border: '1px dashed #94a3b8',
                  background: '#f8fafc', marginTop: 8,
                }}>
                  <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                    Add a new village to {form.taluka}, {form.district}, {form.state}
                  </p>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Village Name <span className="required">*</span></label>
                      <input type="text" className={`form-input ${errors.newVillageName ? 'error' : ''}`}
                        value={newVillageName}
                        onChange={(e) => { setNewVillageName(e.target.value); if (errors.newVillageName) setErrors(prev => ({ ...prev, newVillageName: '' })); }}
                        placeholder="e.g. Rampur Kalan" />
                      {errors.newVillageName && <span className="error-message">{errors.newVillageName}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Village Code (optional)</label>
                      <input type="text" className="form-input"
                        value={newVillageCode}
                        onChange={(e) => setNewVillageCode(e.target.value)}
                        placeholder="e.g. 123456" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button type="button" className="btn btn-primary" disabled={isAddingVillage}
                      onClick={handleAddNewVillage}>
                      {isAddingVillage ? 'Adding...' : 'Add Village'}
                    </button>
                    <button type="button" className="btn btn-secondary" disabled={isAddingVillage}
                      onClick={() => { setShowAddVillage(false); setNewVillageName(''); setNewVillageCode(''); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/villages/list')}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={!pickedVillage} onClick={handleDone}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddVillage;